import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./slider.module.css";
import React, {
  Dispatch,
  SetStateAction,
  TouchEvent,
  useCallback,
  useState,
} from "react";
import { IDatedText, IVideo } from "../../types/types";
import Video from "../video/video";
import { resolveFileUrl } from "../../utils/fileUrl";
import i18n from "../../i18n";

/**
 * Parses text for markdown-style links [text](url) and plain URLs,
 * returns React nodes with clickable <a> tags
 */
const parseLinksInText = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  // Regex to match [text](url)
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // Markdown-style link [text](url) — only allow safe protocols
    const url = match[2];
    const isSafeUrl = /^https?:\/\//i.test(url);
    parts.push(
      isSafeUrl ? (
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {match[1]}
        </a>
      ) : (
        <span key={match.index} className={styles.link}>{match[1]}</span>
      )
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

function Slider({
  items,
  className,
  activeIndex,
  setActiveIndex,
}: {
  items: string[] | IVideo[] | IDatedText[];
  className: string;
  activeIndex: number;
  setActiveIndex: Dispatch<SetStateAction<number>>;
}) {
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
const isRTL = i18n.dir() === "rtl";

  const handlePrev = useCallback(() => {
    setActiveIndex((prevIndex: number) =>
      prevIndex === 0 ? items.length - 1 : prevIndex - 1
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);
  const handleNext = useCallback(() => {
    setActiveIndex((prevIndex) =>
      prevIndex === items.length - 1 ? 0 : prevIndex + 1
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);
  const handleTouchStart = (event: TouchEvent) => {
    if (touchStartX) return;
    setTouchStartX(event.touches[0].clientX);
  };
  const handleTouchEnd = (event: TouchEvent) => {
    if (touchStartX === null) return;
    const currentX = event.changedTouches[0]?.clientX;
    const diffX = touchStartX - currentX;

    if (diffX > 50) {
      handlePrev();
    } else if (diffX < -50) {
      handleNext();
    }
    setTouchStartX(null);
  };


  return (
    <div className={styles.carouselWrapper}>
      <div  dir={i18n.dir()} className={styles.carouselItems} style={{
    transform: `translateX(${(isRTL ? 1 : -1) * activeIndex * 100}%)`
,   }}>
        {items.map((item, index: number) => {
          if (typeof item === "string") {
            return (
              <div
                key={index}
                onTouchStart={handleTouchStart}
                onTouchEnd={(e) => handleTouchEnd(e)}
                className={`${styles.carouselItem} `}
                style={{
                  // transform: `translateX(${activeIndex * 100}%)`,
                }}
              >
                <p className={className}>{item}</p>
              </div>
            );
          } else if ("text" in item) {
            return (
              <div
                key={index}
                onTouchStart={handleTouchStart}
                onTouchEnd={(e) => handleTouchEnd(e)}
                className={`${styles.carouselItem} `}
                style={{
                  // transform: `translateX(${activeIndex * 100}%)`,
                }}
              >
                {item.link ?
                  (<p className={className} style={{ whiteSpace: "pre-line" }}>{item.text} <a href={resolveFileUrl(item.link)} target="_blank" rel="noreferrer">{item.linkText}</a></p>)
                  : (<p className={className} style={{ whiteSpace: "pre-line" }}>{parseLinksInText(item.text)}</p>)
                }
              </div>
            );
          } else {
            return (
              <li
                onTouchStart={handleTouchStart}
                onTouchEnd={(e) => handleTouchEnd(e)}
                style={{
                  // transform: `translateX(${activeIndex * 100}%)`,
                }}
                key={index}
                className={`${styles.slider__sectionCard} ${styles.slider__sectionCard_centered}`}
              >
                <div className={styles.slider__sectionContent}>
                  <p
                    className={`${styles.slider__sectionText} ${styles.slider__sectionText_centered} ${styles.slider__sectionText_small}`}
                  >
                    {item.title}
                  </p>
                  <Video src={item.link} />
                </div>
              </li>
            );
          }
        })}
      </div>
    </div>
  );
}

export default Slider;